import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useResolvedOwnerId } from '@/hooks/analytics/useResolvedOwnerId';
import {
  type PromoteWorkspaceLearningInput,
  workspaceLearningService,
} from '@/services/data/WorkspaceLearningService';

export const workspaceLearningsKey = (
  ownerId: string | null | undefined,
  brandKitId: string | null | undefined,
) => ['workspace-learnings', ownerId ?? null, brandKitId ?? null] as const;

export const readoutWorkspaceLearningsKey = (
  ownerId: string | null | undefined,
  readoutIds: string[],
) => ['workspace-learning-history', ownerId ?? null, [...readoutIds].sort()] as const;

/**
 * Historical read model for the Brief that produced a learning. This must
 * include inactive rows so lifecycle actions never rewrite history as
 * “Not promoted.” Active prompt/Brief injection continues to use
 * `useWorkspaceLearnings` and its expiry filter.
 */
export function useReadoutWorkspaceLearnings(readoutIds: string[]) {
  const stableIds = [...new Set(readoutIds.filter(Boolean))].sort();
  const { data: ownerId } = useResolvedOwnerId(stableIds.length > 0);

  return useQuery({
    queryKey: readoutWorkspaceLearningsKey(ownerId, stableIds),
    enabled: Boolean(ownerId && stableIds.length > 0),
    queryFn: () => workspaceLearningService.listForReadouts(stableIds),
  });
}

/**
 * Active learnings for one workspace, plus the single human promotion
 * action. Mirrors `useBriefReadouts.ts`: `workspace_learnings` rows are
 * written only through `promote_workspace_learning` (a human act — see the
 * RPC's own comment) or the service role; there is no direct insert/update
 * path from a component.
 */
export function useWorkspaceLearnings(brandKitId: string | null | undefined) {
  const { data: ownerId } = useResolvedOwnerId(Boolean(brandKitId));
  const queryClient = useQueryClient();
  const queryKey = workspaceLearningsKey(ownerId, brandKitId);

  const query = useQuery({
    queryKey,
    enabled: Boolean(ownerId && brandKitId),
    queryFn: () => workspaceLearningService.listActive(brandKitId!),
  });

  const promote = useMutation({
    mutationFn: (input: PromoteWorkspaceLearningInput) => workspaceLearningService.promote(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({ queryKey: ['workspace-learning-history'] });
    },
  });

  /** Human lifecycle transitions. Expired/contested/superseded rows stop
   * reaching this active query and the agent/Brief context fetcher. */
  const contest = useMutation({
    mutationFn: (learningId: string) => workspaceLearningService.contest(learningId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({ queryKey: ['workspace-learning-history'] });
    },
  });

  const expire = useMutation({
    mutationFn: ({ learningId, expiresAt }: { learningId: string; expiresAt?: string }) =>
      workspaceLearningService.expire(learningId, expiresAt),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({ queryKey: ['workspace-learning-history'] });
    },
  });

  const supersede = useMutation({
    mutationFn: ({ learningId, supersededBy }: { learningId: string; supersededBy: string }) =>
      workspaceLearningService.supersede(learningId, supersededBy),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({ queryKey: ['workspace-learning-history'] });
    },
  });

  const attachToBrief = useMutation({
    mutationFn: ({ briefId, learningId }: { briefId: string; learningId: string }) =>
      workspaceLearningService.attachToBrief(briefId, learningId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['brief-evidence'] });
      void queryClient.invalidateQueries({ queryKey: ['brief-workspace'] });
    },
  });

  return { ...query, promote, contest, expire, supersede, attachToBrief };
}
